import React, { FunctionComponent, useState, useEffect, ElementType } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQuery } from '@tanstack/react-query';

// import { check_subdomain } from '../../lib/server_helper';
import { loginAtom } from '../../lib/auth';
import { currentPracticeAtom } from '../../lib/practice';
import { fetch_all, fetch_all_with_count, fetch_one } from "../../lib/v31lib";

import Background from '../../images/banana-blueberries-bowl-916925.jpg';

import AssignmentIcon from '@mui/icons-material/Assignment';
import AttributionIcon from '@mui/icons-material/Attribution';
import BackupIcon from '@mui/icons-material/Backup';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import BugReportIcon from '@mui/icons-material/BugReport';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import EditNoteIcon from '@mui/icons-material/EditNote';
import Egg from '@mui/icons-material/Egg';
import EmailIcon from '@mui/icons-material/Email';
import EngineeringIcon from '@mui/icons-material/Engineering';
import EventIcon from '@mui/icons-material/Event';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ForumIcon from '@mui/icons-material/Forum';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import GTranslateIcon from '@mui/icons-material/GTranslate';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HiveIcon from '@mui/icons-material/Hive';
import HomeIcon from '@mui/icons-material/Home';
import HotelIcon from '@mui/icons-material/Hotel';
import LanguageIcon from '@mui/icons-material/Language';
import MailLockIcon from '@mui/icons-material/MailLock';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MemoryIcon from '@mui/icons-material/Memory';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import QuizIcon from '@mui/icons-material/Quiz';
import RestaurantMenu from '@mui/icons-material/RestaurantMenu';
import RiceBowl from '@mui/icons-material/RiceBowl';
import SavedSearchIcon from '@mui/icons-material/SavedSearch';
import SchoolIcon from '@mui/icons-material/School';
import SearchIcon from '@mui/icons-material/Search';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import SettingsIcon from '@mui/icons-material/Settings';
import SmsIcon from '@mui/icons-material/Sms';
import TranslateIcon from '@mui/icons-material/Translate';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

import {
  Badge,
  Box,
  Divider,
  List,
  SwipeableDrawer
} from '@mui/material';

import {
  IconButton
} from '../../components/v2/styled';

import { MenuMenuItem } from '../../components/v2/menu/MenuItem';
import { LanguagePicker } from '../../components/v2/LanguagePicker';
import { VersionButton } from '../../components/v2/VersionButton';

import { Bug } from '../../models/Bug';
import { Consent } from '../../models/Consent';
import { MergeRequest } from '../../models/MergeRequest';
import { Practice } from '../../models/Practice';
import { PremiumType } from '../../models/PremiumType';
import { User } from '../../models/User';

import HelpersEhealth from '../../actions/helpers/ehealth';
import HelpersZorgmail from '../../actions/helpers/zorgmail';
import Euro from '@mui/icons-material/Euro';
import GroupWork from '@mui/icons-material/GroupWork';
import Bento from '@mui/icons-material/Bento';

type Props = {
  drawerOpen: boolean;
  setDrawerOpen: (b:boolean) => void;
}

export const SharedSidebar: FunctionComponent<Props> = ({drawerOpen, setDrawerOpen}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  // const nonmobile = useMediaQuery('(min-width:600px)');

  const matchesmd = useMediaQuery(theme.breakpoints.up('md'));
  let count = 0;
  let mergeCount = 0;
  let restrCount = 0;
  let bugCount = 0;
  let knowledgeApprovalCount = 0;

  const {
    data: objects,
    isLoading: objectsLoading,
    isError: objectsError,
    isSuccess: objectsSuccess,
    refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["bugs_count", login?.id],
    queryFn: () =>
      fetch_all_with_count<Bug>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'bug',
          fields: ['id', 'title', 'created_at', 'content', 'bug_type', 'impact', 'get_item', 'item_file_name', 'practice_id', 'user_id', 'owner_id'],
          order: "id ASC",
          per_page: 20,
          page: 0,
          filter: {
            search: '',
            advanced: {
              owner_id: login?.id,
              ping_owner: 1
            }
          }
        },
        login
      ),
    select: (d) => {
      count = d.count;
      return d.result;
    },
    enabled: !!login?.id,
  });
  const {
    data: bugObjects,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["admin_bugs_count", login?.id],
    queryFn: () =>
      fetch_all_with_count<Bug>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'bug',
          fields: ['id'],
          order: "id ASC",
          per_page: 20,
          page: 0,
          filter: {
            search: '',
            advanced: {
              current_status: '0,1'
            }
          }
        },
        login
      ),
      select: (d) => {
        bugCount = d.count;
        return d.result;
      },
      enabled: !!login?.id && login?.type === "User::Admin",
      refetchInterval: 2 * 60 * 1000
  });
  const {
    data: mergeObjects,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: mergeObjectsRefetch,
  } = useQuery({
    queryKey: ["merges_count", login?.id],
    queryFn: () =>
      fetch_all_with_count<MergeRequest>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'merge_request',
          fields: ['id'],
          order: "id ASC",
          per_page: 20,
          page: 0,
          filter: {
            search: '',
            advanced: {
              admin_only: 1,
              current_status: "0,1"
            }
          }
        },
        login
      ),
    select: (d) => {
      mergeCount = d.count;
      return d.result;
    },
    enabled: !!login?.id && login?.type === "User::Admin",
    refetchInterval: 2 * 60 * 1000
  });
  const {
    data: restrObjects,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: mergeObjectsRefetch,
  } = useQuery({
    queryKey: ["restricteds_count", login?.id],
    queryFn: () =>
      fetch_all_with_count<User>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'restricted',
          fields: ['id'],
          order: "id ASC",
          per_page: 20,
          page: 0,
          filter: {
            search: '',
            advanced: {
              admin_only: 1,
              checked: 0
            }
          }
        },
        login
      ),
    select: (d) => {
      restrCount = d.count;
      return d.result;
    },
    enabled: !!login?.id && login?.type === "User::Admin",
    refetchInterval: 2 * 60 * 1000
  });
  const {
    data: knowledgeApprovalObjects,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: mergeObjectsRefetch,
  } = useQuery({
    queryKey: ["knowledge_approval_count", login?.id],
    queryFn: () =>
      fetch_all_with_count<User>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'knowledge_subscription',
          fields: ['id'],
          order: "id ASC",
          per_page: 20,
          page: 0,
          filter: {
            search: '',
            advanced: {
              needs_approval: 1,
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
    select: (d) => {
      knowledgeApprovalCount = d.count;
      return d.result;
    },
    enabled: !!login?.id && login?.type === "User::Coach",
    refetchInterval: 2 * 60 * 1000
  });
  const {
    data: practice,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["practice_fetch", login?.practice_id],
    queryFn: () =>
      fetch_one<Practice>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'practice',
          fields: ['id', 'custom_theme', 'get_item', 'settings', 'name', 'item_is_square', 'get_medium', 'group_move', 'group_eat', 'modules', 'get_description', 'url_path'],
          sub_fields: {
            modules: ['id', 'name']
          },
          id: login?.practice_id
        },
        login
      ),
    enabled: !!login && (login.type === "User::Coach" || login.type === "User::Restricted") && !!login?.practice_id,
  });
  const {
    data: myPractices,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["my_practices_fetch_sidebar", login?.practice_id],
    queryFn: () =>
      fetch_all<Consent>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'consent',
          fields: ['id', 'practice', 'enabled'],
          sub_fields: {
            practice: ['id', 'name', 'get_medium']
          },
          filter: {
            search: '',
            advanced: {
              user_id: login?.id
            }
          }
        },
        login
      ),
    enabled: !!login && login.type === "User::User"
  });








  useEffect(() => {
    if (!!practice) {
      setCurrentPractice(practice);
    }
  }, [!!practice]);

  let is_admin = login && login.type === "User::Admin";
  let is_coach = login && login.type === "User::Coach";
  let is_client = login && login.type === "User::User";
  let is_restricted = login && login.type === "User::Restricted";

  let menu:{
    title: string,
    path: string,
    icon: ElementType,
    iconPath?: string,
    hide?: boolean,
    disabled?: boolean,
    badge?: number;
    check_premium?: PremiumType,
    is_premium?: boolean,
    children?: {
      title: string,
      path: string,
      icon: ElementType,
      iconPath?: string,
      hide?: boolean,
      disabled?: boolean,
      check_premium?: PremiumType,
      is_premium?: boolean;
      badge?: number;
    }[]
  }[] = [];

  if (is_admin) {
    menu = [
      {title: t("admin.menu.home"), icon: HomeIcon, path: "/"},
      {title: t("admin.menu.merge_requests"), icon: CallMergeIcon, path: "/merge_requests", badge: (mergeCount || 0)},
      {title: t("admin.menu.content", "Content"), icon: FolderSpecialIcon, path: '', children: [
        {title: t("admin.menu.coach_skills"), icon: ManageAccountsIcon, path: "/coach_skills"},
        {title: t("admin.menu.practice_types"), icon: MemoryIcon, path: "/practice_types"},
        {title: t("admin.menu.statics", "Statics"), icon: EditNoteIcon, path: "/statics"},
        {title: t("admin.menu.translations", "Vertalingen"), icon: LanguageIcon, path: "/translations", hide: true},
      ]},
      {title: t("admin.menu.food_calculation"), icon: RestaurantMenu, path: '', children: [
        {title: t("admin.menu.adh_imports"), icon: FactCheckIcon, path: "/adh_imports"},
        {title: t("admin.menu.nevo_imports"), icon: CloudDownloadIcon, path: "/nevo_imports"},
        {title: t("admin.menu.adh_profiles"), icon: Bento, path: "/adh_profiles"},
        {title: t("admin.menu.foodstuff_measures"), icon: RiceBowl, path: "/foodstuff_measures"},
        {title: t("admin.menu.foodstuffs"), icon: Egg, path: "/foodstuffs"},
        {title: t("admin.menu.nutritional_values"), icon: GroupWork, path: "/nutritional_values"},
      ]},
      {title: t("admin.menu.email_templates"), icon: MailLockIcon, path: "/email_templates"},
      {title: t("admin.menu.imports"), icon: BackupIcon, path: "/imports"},
      {title: t("admin.menu.platform", "Platform"), icon: EngineeringIcon, path: '', children: [
        {title: t("admin.menu.cdn", "CDN"), icon: ContentPasteSearchIcon, path: "/cdn", hide: true},
        {title: t("admin.menu.countries", "Landen"), icon: PublicIcon, path: "/countries"},
        {title: t("admin.menu.modules", "Modules"), icon: ViewModuleIcon, path: "/modules"},
        {title: t("admin.menu.languages", "Talen"), icon: TranslateIcon, path: "/languages"},
      ]},
      {title: t("admin.menu.practices"), icon: BusinessIcon, path: "/practices"},
      {title: t("admin.menu.news_items"), icon: NewspaperIcon, path: "/news_items"},
      {title: t("admin.menu.restricteds"), icon: AttributionIcon, path: "/restricteds", badge: (restrCount || 0)},
      {title: t("admin.menu.sms"), icon: SmsIcon, path: "/sms"},
      {title: t("admin.menu.translations"), icon: GTranslateIcon, path: "/translations"},
      {title: t("admin.menu.user_page"), icon: GroupsIcon, path: "/user_page"},
    ];
  }
  if (is_coach) {
    let term_workshop = t("coach.menu.workshops", "Workshops");
    let is_nutri_appointment = false;
    let is_nutri_workshop = false;
    let is_nutri_mail = false;
    let is_questionnaire = false;
    let is_knowledge = false;
    let is_payments = false;

    if (currentPractice) {
      // Modules
      (currentPractice.modules || []).forEach(e => {
        if (!is_nutri_appointment) is_nutri_appointment = e.id === 1;
        if (!is_nutri_workshop) is_nutri_workshop = e.id === 2;
        if (!is_nutri_mail) is_nutri_mail = e.id === 13;
        if (!is_payments) is_payments = e.id === 15;
        if (!is_questionnaire) is_questionnaire = e.id === 19;
        if (!is_knowledge) is_knowledge = e.id === 20;
      });

      // Terms
      if (!!currentPractice?.settings?.workshops_plural) {
        term_workshop = currentPractice.settings.workshops_plural;
      }
    }
    let is_ehealth = HelpersEhealth.can_check_ehealth(login);
    let is_zorgmail = HelpersZorgmail.can_check_zorgmail(login);
    let can_check_ehealth = is_ehealth;

    menu.push({title: t("coach.menu.home"), icon: HomeIcon, path: "/"});
    menu.push({title: t("coach.menu.nutri_appointment", "Afspraken"), icon: CalendarMonthIcon, path: "/agenda", check_premium: 'nutri_appointment', is_premium: is_nutri_appointment});
    // menu.push({title: t("coach.menu.nutri_appointment", "Afspraken"), icon: CalendarMonthIcon, path: '', children: [
    //   {title: t("coach.menu.appointments", "Afspraken"), icon: FormatListBulletedIcon, path: "/agenda", check_premium: 'nutri_appointment', is_premium: is_nutri_appointment},
    //   {title: term_workshop, icon: CoPresentIcon, path: "/workshops", check_premium: 'nutri_workshop', is_premium: is_nutri_workshop}
    // ]});
    menu.push({title: t("coach.menu.clients", "Cliënten"), icon: GroupsIcon, path: '', children: [
      {title: t("coach.menu.users", "Cliënten"), icon: PeopleIcon, path: "/users"},
      {title: t("coach.menu.merge_requsts", "Dubbels"), icon: CallMergeIcon, path: "/merge_requests", hide: !login?.is_super},
      {title: t("coach.menu.logs", "Logs"), icon: AssignmentIcon, path: "/logs", hide: !!login?.is_secretary},
      // {title: t("coach.menu.questionnaires", "Vragenlijsten"), icon: QuizIcon, path: "/questionnaires", hide: !!login?.is_secretary, check_premium: 'questionnaire', is_premium: is_questionnaire}
    ]});
    menu.push({title: t("coach.menu.communication"), icon: ForumIcon, path: '', children: [
      {title: t("coach.menu.nutri_mail", "NutriMail"), icon: EmailIcon, path: "/inbox", check_premium: 'nutri_mail', is_premium: is_nutri_mail},
      {title: t("coach.menu.ehealth_mail", "EhealthMail"), icon: ContactMailIcon, path: "/ehealth_inbox", hide: !can_check_ehealth, check_premium: 'ehealth', is_premium: !!is_ehealth},
      {title: t("coach.menu.zorg_mail", "ZorgMail"), icon: MailLockIcon, path: "/zorgmail_inbox", check_premium: 'zorg_mail', is_premium: is_zorgmail},
      {title: t("coach.menu.news_items", "Nieuwsberichten"), icon: NewspaperIcon, path: "/news_items", hide: !login?.is_super}
    ]});
    menu.push({title: t("coach.menu.advanced"), icon: HiveIcon, path: '', children: [
      {title: term_workshop, icon: CoPresentIcon, path: "/workshops", check_premium: 'nutri_workshop', is_premium: is_nutri_workshop},
      {title: t("coach.menu.mollie_links", "Betaallinks"), icon: Euro, path: "/mollie_links", check_premium: 'payments', is_premium: is_payments},
      {title: t("coach.menu.questionnaires", "Vragenlijsten"), icon: QuizIcon, path: "/questionnaires", hide: !!login?.is_secretary, check_premium: 'questionnaire', is_premium: is_questionnaire},
      {title: t("coach.menu.knowledge_courses", "Cursussen"), icon: SchoolIcon, path: "/knowledge_courses", badge: (knowledgeApprovalCount || 0), check_premium: 'knowledge', is_premium: is_knowledge},
      {title: t("coach.menu.segments", "Segmenten"), icon: GroupWorkIcon, path: "/segments"}
    ], badge: (knowledgeApprovalCount || 0)});
    menu.push({title: t("coach.menu.selfcare"), icon: SelfImprovementIcon, path: '', children: [
      {title: t("coach.menu.knowledge_center", "Kennisomgeving"), icon: SchoolIcon, path: "/knowledge_center"},
      {title: t("coach.menu.my_questionnaires", "Mijn Vragenlijsten"), icon: QuizIcon, path: "/my_questionnaires"}
    ]});
    // menu.push({title: t("coach.menu.knowledge_center", "Kennisomgeving"), icon: SchoolIcon, path: "/knowledge_center"});
    menu.push({title: t("coach.menu.exports", "Exports"), icon: CloudDownloadIcon, path: "/exports", hide: !!login?.is_secretary});
    menu.push({title: t("coach.menu.settings_parent"), icon: SettingsIcon, path: '', children: [
      {title: t("coach.menu.personal_settings", "Instellingen"), icon: ManageAccountsIcon, path: "/settings"},
      {title: t("coach.menu.settings", "Instellingen"), icon: EngineeringIcon, path: "/general_settings", hide: !login?.is_super}
    ]});
    // menu.push({title: t("coach.menu.friend_links", "Friends"), icon: DatasetLinkedIcon, path: "/friend_links", hide: !login?.is_super});
  }
  if (is_client) {
    let term_workshop = t("client.menu.workshops", "Workshops");
    let is_practice_chosen = false;
    let is_nutri_appointment = false;
    let is_nutri_workshop = false;

    if (currentPractice) {
      is_practice_chosen = true;

      // Modules
      (currentPractice.modules || []).forEach(e => {
        if (!is_nutri_appointment) is_nutri_appointment = e.id === 1;
        if (!is_nutri_workshop) is_nutri_workshop = e.id === 2;
      });

      // Terms
      if (!!login?.override_theme?.workshops_plural) {
        term_workshop = login?.override_theme.workshops_plural;
      } else {
        // if (!!currentPractice?.settings?.workshops_plural) {
        //   term_workshop = currentPractice.settings.workshops_plural;
        // }
      }
    }

    menu.push({title: t("client.menu.home"), icon: HomeIcon, path: "/"});
    menu.push({title: t("client.menu.diary"), icon: MenuBookIcon, path: "/diary"});
    menu.push({title: t("client.menu.measures"), icon: DesignServicesIcon, path: '', children: [
      {title: t("client.menu.blood_uploads"), icon: BloodtypeIcon, path: "/blood"},
      {title: t("client.menu.measure_wake"), icon: HotelIcon, path: "/wake"},
      {title: t("client.menu.measure_weight"), icon: MonitorWeightIcon, path: "/weight", hide: !!login?.extra_data?.weight_trigger}
    ]});
    // if (!!login?.practice_id) {
    //   menu.push({title: t("client.menu.practice"), icon: SavedSearchIcon, path: "/practice"});
    // } else {
    //   menu.push({title: t("client.menu.find"), icon: SearchIcon, path: "/find"});
    // }
    menu.push({title: t("client.menu.agenda", "Mijn Agenda"), icon: CalendarMonthIcon, path: '', children: [
      {title: t("client.menu.appointments", "Afspraken"), icon: FormatListBulletedIcon, path: "/appointments"},
      {title: t("client.menu.appointment", "Maak afspraak"), icon: EventIcon, path: "/appointment"},
      {title: term_workshop, icon: CoPresentIcon, path: "/workshops"}
    ]});
    menu.push({title: t("client.menu.intake"), icon: ContactEmergencyIcon, path: "/intake"});
    menu.push({title: t("client.menu.questionnaires"), icon: QuizIcon, path: "/questionnaires"});
    menu.push({title: t("client.menu.knowledge_center"), icon: SchoolIcon, path: "/knowledge_center"});
    menu.push({title: t("client.menu.nutri_mail", "NutriMail"), icon: EmailIcon, path: "/inbox"});
    menu.push({title: t("client.menu.third_party", "Partners"), icon: HandshakeIcon, path: '', children: [
      {title: t("client.menu.synapse", "Synapse"), icon: HandshakeIcon, iconPath: "https://nutriportal.synapse-lifestyle.com/static/favicon.ico", path: "/3p/synapse"},
    ]});
    menu.push({title: t("client.menu.practices"), icon: BusinessIcon, path: '', children: [
      ...((myPractices || []).filter(fff => !!fff.enabled).map(consent => {
        return {
          title: (consent.practice?.name || ''), 
          icon: SavedSearchIcon, 
          iconPath: consent?.practice?.get_medium,
          path: `/practice/${consent?.practice?.id}`
        };
      })),
      {title: t("client.menu.find"), icon: SearchIcon, path: "/find"}
    ]});
    menu.push({title: t("client.menu.settings", "Instellingen"), icon: SettingsIcon, path: "/settings"});
  }

  if (is_restricted) {
    menu.push({title: t("restricted.menu.home"), icon: HomeIcon, path: "/"});
    menu.push({title: t("restricted.menu.communication"), icon: ForumIcon, path: '', children: [
      {title: t("restricted.menu.nutri_mail", "NutriMail"), icon: EmailIcon, path: "/inbox", disabled: !login?.extra_data?.restricted_saved},
      {title: t("restricted.menu.ehealth_mail", "EhealthMail"), icon: ContactMailIcon, path: "/ehealth_inbox", disabled: !login?.extra_data?.restricted_saved},
      // {title: t("coach.menu.zorg_mail", "ZorgMail"), icon: MailLockIcon, path: "/zorgmail_inbox", check_premium: 'zorg_mail', is_premium: is_zorgmail},
      {title: t("restricted.menu.news_items", "Nieuwsberichten"), icon: NewspaperIcon, path: "/news"}
    ]});
    menu.push({title: t("restricted.menu.knowledge_center"), icon: SchoolIcon, path: "/knowledge_center"});
    // menu.push({title: t("restricted.menu.nutri_mail", "NutriMail"), icon: EmailIcon, path: "/inbox", disabled: !login?.extra_data?.restricted_saved});
    menu.push({title: t("restricted.menu.settings", "Instellingen"), icon: SettingsIcon, path: "/settings"});
  }

  return <>
    <SwipeableDrawer
      onClose={() => {
        setDrawerOpen(false);
      }}
      onOpen={() => {
        setDrawerOpen(true);
      }}
      open={(matchesmd || drawerOpen)}
      variant={matchesmd ? "permanent" : "temporary"}
      anchor='left'
      sx={{
        height: '100%',
        paddingTop: {
          xs: '56px',
          md: '75px'
        },
        zIndex: 1000,
        position: 'fixed',
        "& .MuiDrawer-paper": {
          position: 'relative',
          whiteSpace: 'nowrap',
          width: {
            xs: '90%',
            md: 240
          },
          transition: theme ? theme.transitions.create('width', {
            easing: 'cubic-bezier(0.4, 0, 0.6, 1)',
            duration: 225
          }) : {},
          paddingTop: {
            xs: 0,
            md: 3
          },
          ...(!drawerOpen && !matchesmd ? {
            overflowX: 'hidden',
            transition: theme ? theme.transitions.create('width', {
              easing: 'cubic-bezier(0.4, 0, 0.6, 1)',
              duration: 195
            }) : {},
            width: 0,
          } : {})
        }
      }}
    >
      <Box sx={{
        backgroundPosition: "bottom left",
        backgroundSize: "cover",
        top: 0,
        width: "100%",
        height: "100%",
        backgroundImage: `url(${Background})`,
        position: "absolute"
      }}></Box>
      <Box sx={{
        backgroundColor: "rgba(255,255,255,0.8)",
        position: "absolute",
        top: 0,
        width: "100%",
        height: "100%"
      }}></Box>

      <Box sx={{
        height: "100%",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column"
      }}>
        <Box sx={{flexGrow: 1}}>
          <List>
            {menu.map((item, i) => <MenuMenuItem
              key={i}
              path={item.path}
              title={item.title}
              Icon={item.icon}
              iconPath={item.iconPath}
              children={item.children}
              setDrawerOpen={setDrawerOpen}
              hide={item.hide}
              badge={item.badge}
              disabled={item.disabled}
              check_premium={item.check_premium}
              is_premium={item.is_premium}
            />)}
            {/* {menu.map((x, i) => (x.children ? <ListItemButtonWithChildren key={`menu_${i}`} {...this.props} {...x}/> : <ListItemButton key={`menu_${i}`} {...this.props} {...x}/>))} */}
          </List>
          <Divider />
        </Box>
        <Box sx={{position: 'relative'}}>
          <List>
            <VersionButton
              url={!login || (!!login && login.type !== "User::User") ? "http://www.nutrilink.eu/" : "http://www.nutriportal.be/"}
            />
          </List>

          {!!login && <IconButton
            title={t("general.bugs.tooltip", "Rapporteer een bug, of stel een feature voor")}
            onClick={() => {
              navigate("/bugs");
              setDrawerOpen(false);
            }}
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 80,
              padding: 1
            }}
          >
            <Badge 
              badgeContent={login?.type === "User::Admin" ? bugCount : count}
              color="primary"
            >
              <ConfirmationNumberIcon color="secondary" />
            </Badge>
          </IconButton>}

          <LanguagePicker sx={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            padding: 1
          }} />
        </Box>
      </Box>
    </SwipeableDrawer>
  </>;
}

